'use client';

import Cookie from 'js-cookie';
import moment from 'moment';
import { signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next-nprogress-bar';
import { usePathname, useSearchParams } from 'next/navigation';
import { ReactNode, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { useIdleTimer } from 'react-idle-timer';
import { ThemeProvider } from 'styled-components';

import { ToastController } from '@components';
import { TipoSessaoEnum } from '@enums/tipo-sessao.enum';
import { useBetterMediaQuery } from '@helpers';
import { tema } from '@styles/theme';

import { clientIdGovBR, urlRedirectGovBR } from 'src/utils/infra.utils';

import StyledComponent from './configuracao-styled-component';
import { LogoutProvider } from './loggout-provider';

export const Providers = ({ children }: { children: ReactNode }) => {
  const rota = useRouter();

  const { data: session, update } = useSession();
  const url = usePathname();
  const tempoDeInatividade = 900000;
  const navigation = useSearchParams();
  const msg = navigation?.get('msg');

  const verificarInatividadeDoUsuario = async () => {
    localStorage.removeItem('lastTimeCheck');
    deslogar();
  };

  const verificarUsuarioAtivo = () => {
    const lastTimeCheck = localStorage.getItem('lastTimeCheck');

    if (
      lastTimeCheck &&
      moment().unix() >
        moment.unix(Number(lastTimeCheck)).add(Number(tempoDeInatividade), 'milliseconds').unix()
    ) {
      verificarInatividadeDoUsuario();

      return;
    }

    localStorage.setItem('lastTimeCheck', moment().unix().toString());
    reset();
  };

  const { isIdle, reset } = useIdleTimer({
    onIdle: verificarInatividadeDoUsuario,
    onAction: verificarUsuarioAtivo,
    timeout: tempoDeInatividade,
    throttle: 500,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
      'visibilitychange',
      'focus',
      'pagehide',
      'suspend',
    ],
  });

  const deslogar = async (atribuirCookie = true) => {
    if (session) {
      if (atribuirCookie) {
        sessionStorage.setItem('sessao', 'sessao-expirada');
      }

      if (
        session?.user.tipo &&
        [TipoSessaoEnum.ENVIO_DE_MATERIA, TipoSessaoEnum.ASSINATURA].includes(session?.user.tipo)
      ) {
        await signOut({
          redirect: false,
        }).then(() => {
          rota.push(
            `${
              process.env.NEXT_PUBLIC_LOGIN_UNICO_API
            }/sso/sair?client_id=${clientIdGovBR}&redirect_uri=${encodeURI(urlRedirectGovBR)}`,
          );
        });
      } else {
        localStorage.setItem('logout-event', Date.now().toString());
        await signOut({ callbackUrl: '/' });
      }
    }
  };

  useEffect(() => {
    isIdle();
  }, [update, verificarInatividadeDoUsuario, verificarUsuarioAtivo]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (url !== '/solicitar-credenciamento/formulario') {
      sessionStorage.removeItem('formulario-credenciamento');
    }

    if (url !== '/gerenciamento/diarios') {
      sessionStorage.removeItem('diario-selecionado');
    }
  }, [url]);

  const dispositivoMobile = useBetterMediaQuery('(max-width: 1024px)');

  useEffect(() => {
    const toastMsg = Cookie.get('toast-message');
    if (toastMsg) {
      ToastController.aviso(toastMsg);
      Cookie.remove('toast-message');
    }
  }, [msg]);

  return (
    <LogoutProvider>
      <StyledComponent>
        <ThemeProvider theme={tema}>
          <Toaster
            position='top-right'
            containerClassName='conteiner-toast'
            containerStyle={{
              top:
                dispositivoMobile || url?.slice(0, 6) === '/admin'
                  ? 60
                  : url?.slice(0, 6) === '/envio'
                  ? 20
                  : 80,
            }}
            toastOptions={{
              style: {
                background: 'unset',
                boxShadow: 'unset',
                width: '100%',
                maxWidth: 368,
                margin: 0,
                padding: 0,
              },
            }}
          />
          {children}
        </ThemeProvider>
      </StyledComponent>
    </LogoutProvider>
  );
};
